// app/javascript/packs/react_app.js
import React, { useEffect, useState } from "react";
import "@rails/ujs";
import "@rails/activestorage";
import "@rails/actioncable";
import "../src/channels"
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider, useIsFetching } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import Routing from "../src/routes";
import VsNavLower from "../src/shell/VsNavLower.jsx";
import { historyEntryUrl } from "../src/shell/modeToggleNav";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      cacheTime: 0,
      gcTime: 0,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
    },
  },
});

function showLoader() {
  const el = document.getElementById("react-loader");
  if (el) el.style.display = "block";
}

function GlobalLoaderSync() {
  const isFetching = useIsFetching();
  const [initialSettled, setInitialSettled] = useState(false);

  useEffect(() => {
    const loader = document.getElementById("react-loader");
    if (!loader) return;
    if (!initialSettled && isFetching === 0) {
      setInitialSettled(true);
      loader.style.display = "none";
    }
  }, [isFetching, initialSettled]);

  return null;
}

let reactRoot = null;
let navLowerRoot = null;

// ── Mount the nav lower React component into #vs-nav-lower ─────────────────
// This mounts on every turbolinks:load (nav is present on every logged-in page).
function mountNavLower() {
  const slot = document.getElementById("vs-nav-lower");
  if (!slot) return;
  if (navLowerRoot) {
    try { navLowerRoot.unmount(); } catch (_e) {}
    navLowerRoot = null;
  }
  navLowerRoot = ReactDOM.createRoot(slot);
  navLowerRoot.render(<VsNavLower />);
}

function unmountNavLower() {
  if (navLowerRoot) {
    try { navLowerRoot.unmount(); } catch (_e) {}
    navLowerRoot = null;
  }
}

function mountReact() {
  mountNavLower();
  const node = document.getElementById("react-root");
  if (!node) return;

  showLoader();

  const companyId = node.getAttribute("company-id") || null;
  const companySlug = node.getAttribute("company-slug") || null;

  queryClient.clear(); // Prevent stale cache flash

  reactRoot = ReactDOM.createRoot(node);
  reactRoot.render(
    <QueryClientProvider client={queryClient}>
      <GlobalLoaderSync />
      <Routing companyId={companyId} companySlug={companySlug} />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </QueryClientProvider>
  );
}

function unmountReact() {
  unmountNavLower();
  if (reactRoot) {
    reactRoot.unmount();
    reactRoot = null;
  }
  showLoader();
}

document.addEventListener("turbolinks:load", mountReact);
document.addEventListener("turbolinks:before-cache", unmountReact);

document.addEventListener("turbolinks:before-visit", () => {
  const node = document.getElementById("react-root");
  if (node) {
    // Item #7 taste (i): this normalization used to strip the query string, so BROWSER back from
    // the library landed on plain /activity in Standard chrome — the agent session was gone.
    // historyEntryUrl keeps the search whenever it carries mode=agent (and ONLY then).
    const url = historyEntryUrl(window.location.pathname, window.location.search);
    history.replaceState({ turbolinks: true, url }, "", url);
  }
});
